import AxiosService from "plugins/TS-lib-utils-public/services/AxiosService";

/**
 * @class DoctorsService
 * @extends AxiosService
 * @description Service layer for the doctors management API (`aspect-users`).
 * Covers paginated list retrieval, on-demand detail fetching with server-side
 * sub-document pagination and filtering, statistics, and profile updates.
 */
class DoctorsService extends AxiosService {
  constructor() {
    super("aspect-users");
  }

  /**
   * Retrieves a paginated list of doctor users.
   * Response shape: `{ data: { total: number, data: Doctor[] } }`
   * Each Doctor contains `total_practices` (number) but NOT `practices` array.
   *
   * @param {number} page - Zero-based page index.
   * @param {number} limit - Number of records per page.
   * @param {string|null} [search=null] - Optional full-text search term.
   * @returns {Promise<import("axios").AxiosResponse>}
   */
  getUserDoctor = async (page, limit, search = null) => {
    const base = `/list?page=${page}&limit=${limit}`;
    const query = search
      ? `${base}&search=${encodeURIComponent(search)}`
      : base;

    return await this.get(query, {
      headers: { Authorization: `Bearer ${this.getToken()}` },
    });
  };

  /**
   * Retrieves full details for a single doctor, including paginated
   * `practices` and their nested patient sub-documents with server-side filtering.
   *
   * @param {string} userId - The `auth_user_id` of the target doctor.
   * @param {number} [detailsPage=0] - Zero-based practice page index.
   * @param {number} [detailsLimit=50] - Max practices to return per call.
   * @param {number} [subDetailsPage=0] - Zero-based patient page index within the returned practice.
   * @param {number} [subDetailsLimit=10] - Max patients per practice per call.
   * @param {string} [searchQuery=''] - Optional search term to filter sub-documents on the server.
   * @returns {Promise<import("axios").AxiosResponse>}
   */
  getDoctorDetails = async (
    userId,
    detailsPage = 0,
    detailsLimit = 50,
    subDetailsPage = 0,
    subDetailsLimit = 10,
    searchQuery = ''
  ) => {
    let query = [
      `/list`,
      `?page=0&limit=1`,
      `&target_user_id=${userId}`,
      `&details_page=${detailsPage}`,
      `&details_limit=${detailsLimit}`,
      `&sub_details_page=${subDetailsPage}`,
      `&sub_details_limit=${subDetailsLimit}`,
    ].join("");

    if (searchQuery && searchQuery.trim() !== '') {
      query += `&search=${encodeURIComponent(searchQuery.trim())}`;
    }

    return await this.get(query, {
      headers: { Authorization: `Bearer ${this.getToken()}` },
    });
  };

  /**
   * Retrieves global user statistics aggregated over the given number of days.
   *
   * @param {number} days - Lookback window in days.
   * @returns {Promise<import("axios").AxiosResponse>}
   */
  getAllSystemUsers = async (days) => {
    return await this.get(`/statistics?days=${days}`, {
      headers: { Authorization: `Bearer ${this.getToken()}` },
    });
  };

  /**
   * Submits a management-level profile update for a doctor.
   *
   * @param {Object} data - Profile fields to update. Must include `auth_user_id`.
   * @returns {Promise<import("axios").AxiosResponse>}
   */
  managementUserEdit = async (data) => {
    return await this.put(`/management-edit`, data, {
      headers: { Authorization: `Bearer ${this.getToken()}` },
    });
  };
}

export default new DoctorsService();